import React, { useState } from "react";
import { Sparkles, Clock, ArrowRight, X, Zap } from "lucide-react";
import { Button } from "./ui/button";

const isFilled = (v) => !(v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0));

function relativeTime(iso) {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
}

function shouldShow(onb) {
  if (!onb) return false;
  if (onb.status !== "in_progress") return false;
  if ((onb.visit_count || 1) < 2) return false;
  try {
    const dismissedAt = Number(localStorage.getItem(`welcome-back-dismissed:${onb.id}`) || 0);
    if (Date.now() - dismissedAt < 60 * 60 * 1000) return false; // dismissed within last hour
  } catch (_) { /* SSR / private mode — show */ }
  return true;
}

/**
 * Smart "Welcome back" banner.
 *
 * Shown when an in-progress client returns to their onboarding link.
 * Computes completion progress, last edited time, and offers a one-click
 * "Resume from <section>" CTA. Dismissals are remembered in localStorage
 * for one hour so it doesn't reappear on every save.
 */
export default function WelcomeBackBanner({ onb, sections, data, onResume }) {
  // Lazy initializer — decide visibility once on mount based on onb props at first render
  const [dismissed, setDismissed] = useState(() => !shouldShow(onb));

  if (dismissed || !onb || !sections?.length) return null;

  // Compute completion stats
  const counts = sections.map((s) => {
    const fields = [...(s.fields || []), ...(s.extra_fields || [])];
    const done = fields.filter((f) => isFilled(data?.[s.id]?.[f.key])).length;
    return { id: s.id, title: s.title, done, total: fields.length };
  });
  const sectionsComplete = counts.filter((c) => c.total > 0 && c.done === c.total).length;
  const sectionsTouched = counts.filter((c) => c.done > 0).length;
  const totalFields = counts.reduce((a, c) => a + c.total, 0);
  const filledFields = counts.reduce((a, c) => a + c.done, 0);
  const pct = totalFields ? Math.round((filledFields / totalFields) * 100) : 0;

  // Estimate minutes left: ~25s per remaining field
  const minutesLeft = Math.max(2, Math.round(((totalFields - filledFields) * 25) / 60));

  // Resume target
  const resumeId = onb.current_section || counts.find((c) => c.done < c.total)?.id || sections[0].id;
  const resumeSection = sections.find((s) => s.id === resumeId);

  const firstName = (onb.client_name || onb.client_email || "").split(/[\s@]/)[0] || "there";
  const lastEdited = relativeTime(onb.updated_at || onb.previous_visit_at);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(`welcome-back-dismissed:${onb.id}`, String(Date.now()));
  };

  const handleResume = () => {
    onResume?.(resumeId);
    handleDismiss();
  };

  return (
    <div
      className="relative mb-6 overflow-hidden rounded-2xl border border-[#00C7FF]/30 shadow-lg"
      style={{ background: "linear-gradient(135deg, #0F1644 0%, #1a2270 60%, #0F1644 100%)" }}
      data-testid="welcome-back-banner"
      role="status"
      aria-live="polite"
    >
      {/* decorative glow */}
      <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(0,199,255,0.25), transparent)" }} />
      <div className="pointer-events-none absolute -bottom-16 -left-10 w-56 h-56 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(0,199,255,0.12), transparent)" }} />

      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 z-10 p-1.5 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition"
        aria-label="Dismiss welcome back banner"
        data-testid="welcome-back-dismiss"
      >
        <X size={16} />
      </button>

      <div className="relative px-6 sm:px-8 py-6 sm:py-7 flex flex-col lg:flex-row lg:items-center gap-6">
        {/* Left: greeting + stats */}
        <div className="flex-1 min-w-0">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] font-semibold text-[#00C7FF] mb-2">
            <Sparkles size={14} />
            Welcome back
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold text-white leading-tight">
            Hi {firstName} — pick up where you left off
          </h2>
          <p className="mt-2 text-white/70 text-sm leading-relaxed">
            You&apos;ve completed <span className="text-white font-semibold">{sectionsComplete}</span> of <span className="text-white font-semibold">{sections.length}</span> sections
            {sectionsTouched > sectionsComplete && (
              <> · <span className="text-white">{sectionsTouched - sectionsComplete}</span> in progress</>
            )}
            {lastEdited && (
              <> · last edit <span className="text-white">{lastEdited}</span></>
            )}
          </p>

          {/* progress bar */}
          <div className="mt-4 w-full max-w-md">
            <div className="flex items-center justify-between text-[11px] text-white/60 mb-1.5">
              <span>{filledFields} of {totalFields} fields</span>
              <span className="font-semibold text-[#00C7FF]" data-testid="welcome-back-pct">{pct}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, background: "linear-gradient(90deg, #00C7FF 0%, #6FE5FF 100%)", boxShadow: "0 0 12px rgba(0,199,255,0.6)" }}
              />
            </div>
          </div>

          <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-white/50">
            <Clock size={12} />
            About {minutesLeft} minute{minutesLeft === 1 ? "" : "s"} left to finish
          </div>
        </div>

        {/* Right: resume CTA */}
        <div className="flex-shrink-0 w-full lg:w-auto">
          <div className="text-[11px] uppercase tracking-wider text-white/50 mb-1.5">Resume from</div>
          <div className="text-white font-semibold mb-3 leading-snug max-w-xs truncate" title={resumeSection?.title}>
            {resumeSection?.title || sections[0].title}
          </div>
          <Button
            onClick={handleResume}
            className="font-semibold w-full lg:w-auto group"
            style={{ background: "#00C7FF", color: "#0F1644" }}
            data-testid="welcome-back-resume"
          >
            <Zap size={14} className="mr-1.5" />
            Continue
            <ArrowRight size={14} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
